import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Photo, UploadJob } from '../types/contract';

export function useLivePhotos() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [jobs] = useState<UploadJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPhotos = useCallback(async () => {
    if (!supabase) return;
    
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch photos and assets
      const [photosRes, assetsRes] = await Promise.all([
        supabase
          .from('photos')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase.from('photo_assets').select('*'),
      ]);

      if (photosRes.error) {
        throw new Error('Failed to load photos from backend');
      }
      if (assetsRes.error) {
        throw new Error('Failed to load photo assets from backend');
      }
      const photoData = photosRes.data;
      const assetData = assetsRes.data || [];

      // 2. Map snake_case to camelCase and resolve asset paths
      const assetsByPhoto = new Map<string, any[]>();
      assetData.forEach((asset) => {
        if (!assetsByPhoto.has(asset.photo_id)) {
          assetsByPhoto.set(asset.photo_id, []);
        }
        assetsByPhoto.get(asset.photo_id)!.push(asset);
      });

      const mappedPhotos: Photo[] = (photoData || []).map((p: any) => {
        const assets = assetsByPhoto.get(p.id) || [];
        const previewAsset = assets.find((a) => a.kind === 'preview');
        const thumbnailAsset = assets.find((a) => a.kind === 'thumbnail');

        return {
          id: p.id,
          userId: p.user_id,
          originalFilename: p.original_filename,
          mimeType: p.mime_type,
          capturedAt: p.captured_at,
          latitude: p.latitude,
          longitude: p.longitude,
          altitudeM: p.altitude_m,
          hasGps: p.has_gps,
          status: p.status,
          previewAssetPath: previewAsset?.storage_path || p.preview_asset_path,
          thumbnailAssetPath: thumbnailAsset?.storage_path || p.thumbnail_asset_path,
          createdAt: p.created_at,
          previewUrl: null, // to be populated
        };
      });

      // 3. Get signed URLs for previews
      const pathsToSign = mappedPhotos
        .filter((p) => p.previewAssetPath)
        .map((p) => p.previewAssetPath as string);

      if (pathsToSign.length > 0) {
        const { data: signedUrls, error: signError } = await supabase
          .storage
          .from('photo-media')
          .createSignedUrls(pathsToSign, 60 * 60); // 1 hour expiry

        if (signError) {
          console.warn('Failed to create signed photo preview URLs');
          mappedPhotos.forEach((p) => {
            if (p.previewAssetPath) {
              p.mediaError = 'preview_unavailable';
            }
          });
        } else {
          const urlMap = new Map<string, string>();

          signedUrls?.forEach((su) => {
            if (!su.error && su.signedUrl && su.path) {
              urlMap.set(su.path, su.signedUrl);
            }
          });

          mappedPhotos.forEach((p) => {
            const path = p.previewAssetPath;
            if (path && urlMap.has(path)) {
              p.previewUrl = urlMap.get(path)!;
            } else if (path) {
              p.mediaError = 'preview_unavailable';
            }
          });
        }
      }

      setPhotos(mappedPhotos);
    } catch (err: any) {
      const message = err.message || 'Failed to load photos';
      console.error('Error fetching live photos:', message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  return {
    photos,
    jobs, // Currently not fetching jobs from backend unless needed, keeping local array for now
    loading,
    error,
    refresh: fetchPhotos,
  };
}
